import React, { useState, useEffect, useRef } from "react";
import ChatHeader from "./chat/ChatHeader";
import ChatMessages from "./chat/ChatMessages";
import ChatControls from "./chat/ChatControls";
import { useChat } from "../hooks/useChat";
import useSpeechRecognition from "../hooks/useSpeechRecognition";
import { ToastContainer, Bounce } from "react-toastify";
import VoiceAssistantOverlay from "./VoiceAssistantOverlay";

// NEW imports for analytics and session utils:
import { useAnalytics } from "../analytics/AnalyticsContext";
import { generateSessionId, SessionTimer } from "../utils/sessionUtils";

export default function ChatSidebar() {
  const [isOpen, setIsOpen] = useState(true);
  const [isAssistantOpen, setIsAssistantOpen] = useState(false);

  // Generate a session ID and timer.
  const sessionIdRef = useRef(generateSessionId());
  const sessionTimerRef = useRef(new SessionTimer());
  const { trackEvent, startSession, endSession } = useAnalytics();

  const {
    messages,
    userInput,
    setUserInput,
    model,
    setModel,
    isRecording,
    isPlayingAudio,
    elapsedTime,
    isTyping,
    handleSend,
    stopPlayback,
    startRecording,
    stopRecording,
    isRetrieving,
    handleStopRetrieval,
    chatId,
  } = useChat();

  const { isListening, startListening, stopListening } = useSpeechRecognition({
    onResult: (transcript) => {
      setUserInput(transcript);
    },
    onError: (error) => {
      console.error("Speech recognition error:", error);
      stopRecording();
    },
    continuous: false,
    interimResults: true,
  });

  // Start session when component mounts
  useEffect(() => {
    startSession(sessionIdRef.current, { component: "ChatSidebar" });
    return () => {
      endSession(sessionIdRef.current, {
        totalSessionDurationMs: sessionTimerRef.current.getSessionDuration(),
        chatId,
      });
    };
  }, [startSession, endSession, chatId]);

  function handleRecordToggle() {
    if (isPlayingAudio) return;
    if (!isRecording) {
      startListening();
      startRecording();
      trackEvent("voice_recording_started", { sessionId: sessionIdRef.current });
    } else {
      stopListening();
      stopRecording();
      trackEvent("voice_recording_stopped", { sessionId: sessionIdRef.current });
    }
  }

  function handleSuggestedSend(suggestion) {
    trackEvent("user_message", {
      sessionId: sessionIdRef.current,
      type: "suggested",
      text: suggestion,
      timeSinceLastUserMessageMs: sessionTimerRef.current.getTimeSinceLastUserMessage(),
    });
    sessionTimerRef.current.setLastUserMessageTime();
    handleSend(suggestion);
  }

  function handleModelChange(newModel) {
    setModel(newModel);
    trackEvent("model_changed", { sessionId: sessionIdRef.current, newModel });
  }

  function openVoiceAssistant() {
    setIsAssistantOpen(true);
    trackEvent("voice_assistant_opened", { sessionId: sessionIdRef.current });
  }
  function closeVoiceAssistant() {
    setIsAssistantOpen(false);
    trackEvent("voice_assistant_closed", { sessionId: sessionIdRef.current });
  }

  return (
    <div
      className={`
        flex flex-col
        h-full
        border-r border-gray-200
        bg-white bg-opacity-80
        backdrop-blur-md
        shadow-inner
        transition-all duration-300
        ${isOpen ? "flex-[1]" : "w-14"}
      `}
    >
      <ChatHeader
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        isRecording={isRecording}
        isPlayingAudio={isPlayingAudio}
      />

      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick={false}
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        transition={Bounce}
      />

      {isOpen && (
        <>
          <ChatMessages
            chatId={chatId}
            messages={messages}
            isTyping={isTyping}
            onSendMessage={handleSuggestedSend}
          />

          <ChatControls
            userInput={userInput}
            setUserInput={setUserInput}
            handleSend={(finalText) => {
              trackEvent("user_message", {
                sessionId: sessionIdRef.current,
                type: "typed",
                text: finalText || userInput,
                timeSinceLastUserMessageMs: sessionTimerRef.current.getTimeSinceLastUserMessage(),
              });
              sessionTimerRef.current.setLastUserMessageTime();
              handleSend(finalText || userInput);
            }}
            isRecording={isRecording}
            isPlayingAudio={isPlayingAudio}
            handleRecordToggle={handleRecordToggle}
            handleStopPlayback={stopPlayback}
            elapsedTime={elapsedTime}
            model={model}
            handleModelChange={handleModelChange}
            isRetrieving={isRetrieving}
            handleStopRetrieval={handleStopRetrieval}
            openVoiceAssistant={openVoiceAssistant}
          />
        </>
      )}

      <VoiceAssistantOverlay
        isOpen={isAssistantOpen}
        onClose={closeVoiceAssistant}
        autoStart={true}
      />
    </div>
  );
}
